import { Component, ViewChildren, QueryList } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { GameDayModel } from '../../../models/gameday-model';
import { REPORTS } from '../../gameday.constants';
import { NegativeCasesService } from '../../services/negative-cases.service';
import {
  TopOrdersTableComponent,
  AggregatedOrdersAmount
} from './top-orders-table/top-orders-table.component';
import { GameDayRootReducerState } from '../../../state/root.state';
import { ReportsTabs } from '../../../state/reports/state';
import { getTrancheOrdersFilteredState } from '../../../state/data/reducer';
import { DealWithTrancheOrders } from '../../../state/types';
import { SetActiveAccount } from '../../../state/profile/actions';
import { NegativeCasesState } from '../../../state/negative-cases/state';
import {
  getLinkType,
  NON_ACCOUNT_TYPE
} from '../../mappers/gameDay-orders.transfomations';

@Component({
  selector: 'muni-gameday-top-orders-card',
  templateUrl: './top-orders-card.template.html'
})
export class TopOrdersCardComponent {
  public readonly reportsLink: string = REPORTS;
  public _activeReportsTab: BehaviorSubject<ReportsTabs> = new BehaviorSubject(
    ReportsTabs.Accounts
  );

  @ViewChildren(TopOrdersTableComponent)
  private tables: QueryList<TopOrdersTableComponent>;

  public readonly tranche: Observable<DealWithTrancheOrders> = this.store.pipe(
    select(getTrancheOrdersFilteredState),
    filter(({ orders }) => !!orders)
  );

  public readonly topOrdersByManagers: Observable<
    AggregatedOrdersAmount[]
  > = this.tranche.pipe(
    map((tranche: DealWithTrancheOrders) => {
      const byId = tranche.orders
        .map(({ amount, syndicateDepartment: { id, name, role } }) => ({
          amount,
          id,
          name,
          role
        }))
        .reduce((prev, order: AggregatedOrdersAmount) => {
          const entry = prev[order.id];

          if (entry) {
            entry.amount += order.amount;
          } else {
            prev[order.id] = order;
            prev[order.id].type = NON_ACCOUNT_TYPE;
          }

          return prev;
        }, {});

      tranche.deal.syndicate.forEach(({ id, name, role }) => {
        if (!byId[id]) {
          byId[id] = {
            id,
            amount: 0,
            name,
            type: NON_ACCOUNT_TYPE,
            role
          };
        } else {
          byId[id].name = name;
        }
      });

      return Object.keys(byId).map((id) => byId[id]);
    })
  );

  public readonly topOrdersByAccounts: Observable<
    AggregatedOrdersAmount[]
  > = this.tranche.pipe(
    map((tranche: DealWithTrancheOrders) => {
      const byNames = tranche.orders
        .map((order: GameDayModel.Order) => ({
          id: order.firmId,
          amount: order.amount,
          name: order.accountInfo.name,
          warehouseId: order.warehouseId,
          priority: order.accountInfo.priority
        }))
        .reduce((prev, order: AggregatedOrdersAmount) => {
          const name: string =
            order.priority === GameDayModel.AccountPriority.stock
              ? GameDayModel.STOCK
              : order.name;
          const entry = prev[name];

          if (entry) {
            entry.amount += order.amount;
          } else {
            prev[name] = order;
            prev[name].name = name;
            prev[name].type = getLinkType(order.priority);
          }

          return prev;
        }, {});

      return Object.keys(byNames).map((id) => byNames[id]);
    })
  );

  public readonly managersEmptyMessage: Observable<string> = this.negativeCasesService.messages.pipe(
    map(({ managers }: NegativeCasesState) => managers)
  );

  public readonly accountsEmptyMessage: Observable<string> = this.negativeCasesService.messages.pipe(
    map(({ accounts }: NegativeCasesState) => accounts)
  );

  public readonly reportLinkVisible: Observable<boolean> = combineLatest(
    this.topOrdersByManagers,
    this.topOrdersByAccounts
  ).pipe(
    map(
      ([managerOrders, accountOrders]: [
        AggregatedOrdersAmount[],
        AggregatedOrdersAmount[]
      ]) =>
        (!!managerOrders && managerOrders.length > 0) ||
        (!!accountOrders && accountOrders.length > 0)
    )
  );

  public set activeReportsTab(value: number) {
    this._activeReportsTab.next(
      value === 1 ? ReportsTabs.Accounts : ReportsTabs.ManagersOverview
    );
  }

  constructor(
    private store: Store<GameDayRootReducerState>,
    private negativeCasesService: NegativeCasesService
  ) {}

  public onChange({ payload }) {
    this.tables.forEach((table) => table.resetScroll());
    this.activeReportsTab = payload;
  }

  public setActiveAccount(payload: AggregatedOrdersAmount) {
    this.store.dispatch(new SetActiveAccount(payload.name));
  }
}
